import React, { useEffect, useState } from 'react';
import {
  makeStyles, Typography, Box, Divider, CircularProgress,
} from '@material-ui/core';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import { LocationOn, People, Today } from '@material-ui/icons';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import formatPhoneNumber from '../../helpers/format';
import { fetchStaticToken } from '../../helpers/authentication';
import api from '../../config/api';
import clientCategoryState, { clientTypeState } from '../../state';
import { isLoadingPriceState } from '../../state/apiRequestStatus';
import {
  selectedClinicIdState,
  selectedServiceIdState,
  selectedSubserviceIdState, selectedTimeSlotState, selectedTreatmentIdState,
} from '../../state/selected';
import {
  clinicsState,
  servicesState,
  tarificationIdState,
  therapistsState,
} from '../../state/apiData';

const useStyles = makeStyles((theme) => ({
  cancellationPolicyTitle: {
    color: theme.palette.grey[600],
    marginBottom: theme.spacing(1),
  },
  iconBox: {
    display: 'flex',
  },
  icon: {
    color: theme.palette.common.black,
  },
  iconWrapper: {
    flex: '0 0 40px',
  },
  contentWrapper: {
    paddingTop: '6px',
    flex: '1 0',
  },
  cancellationPolicyText: { color: theme.palette.grey[600] },
  phoneLink: {
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    textDecoration: 'none',
  },
}));

const SelectedTimeSlotDetails = () => {
  const intl = useIntl();
  const classes = useStyles();

  const setTarificationId = useSetRecoilState(tarificationIdState);
  const therapists = useRecoilValue(therapistsState);
  const clinics = useRecoilValue(clinicsState);
  const selectedTreatmentId = useRecoilValue(selectedTreatmentIdState);
  const selectedTimeSlot = useRecoilValue(selectedTimeSlotState);
  const selectedSubserviceId = useRecoilValue(selectedSubserviceIdState);
  const selectedServiceId = useRecoilValue(selectedServiceIdState);
  const selectedClinicId = useRecoilValue(selectedClinicIdState);
  const clientType = useRecoilValue(clientTypeState);
  const clientCategory = useRecoilValue(clientCategoryState);
  const services = useRecoilValue(servicesState);
  const [isLoadingPrice, setIsLoadingPrice] = useRecoilState(isLoadingPriceState);

  const [appointmentPrice, setAppointmentPrice] = useState(null);

  const selectedEmployee = therapists.filter((t) => t.therapistId
    === selectedTimeSlot.employeeID)[0] || null;
  const selectedClinic = clinics.filter((c) => c.id
    === selectedClinicId)[0] || null;
  const selectedService = services.filter((s) => s.externalTherapyID === selectedServiceId)[0];
  const selectedSubservice = selectedService
    .subServices.filter((s) => s.client_type2Id === selectedSubserviceId)[0];

  const fetchPrice = async () => {
    setIsLoadingPrice(true);
    setTarificationId(null);

    const responsePrice = await fetchStaticToken({
      url: api.appointmentPrices,
      method: 'POST',
      body: {
        clinicID: selectedClinicId,
        disciplineID: selectedServiceId,
        subdisciplineID: selectedSubserviceId,
        treatmentID: selectedTreatmentId,
        client_type: clientType,
        client_category: clientCategory,
        date: selectedTimeSlot.fromDayjs.format('YYYY-MM-DD'),
      },
    });

    if (responsePrice.status === 200) {
      const priceData = await responsePrice.json();
      setAppointmentPrice(priceData.price);
      setTarificationId(priceData.tarificationID);
    }

    return setIsLoadingPrice(false);
  };

  const renderUiPrice = () => {
    if (appointmentPrice) {
      return (
        <>
          <strong>
            <FormattedMessage id="SelectedTimeSlotDetails.price" />
          </strong>
          {/* eslint-disable-next-line react/style-prop-object */}
          <FormattedNumber value={appointmentPrice} style="currency" currency="CAD" />
        </>
      );
    }
    return null;
  };

  useEffect(() => {
    fetchPrice();
  }, []);

  return (
    <>
      <Box mb={3}>
        <Typography variant="h5">
          <FormattedMessage id="SelectedTimeSlotDetails.appt" />
          <strong>
            {' '}
            {selectedService.therapyName[intl.locale].toLowerCase()}
            {selectedService && selectedService.subServices.length > 1 && selectedSubservice
              && (
                <>
                  {' / '}
                  {selectedSubservice.subServiceName[intl.locale].toLowerCase()}
                </>
              )}
          </strong>
        </Typography>
      </Box>
      <Box mb={2}>
        <div className={classes.iconBox}>
          <div className={classes.iconWrapper}>
            <Today className={classes.icon} fontSize="large" />
          </div>
          <div className={classes.contentWrapper}>
            <Typography component="div" variant="h6">
              {selectedTimeSlot.fromDayjs.locale(intl.locale)
                .format('LLLL')}
            </Typography>
          </div>
        </div>
      </Box>

      {selectedEmployee && (
        <Box mb={2}>
          <div className={classes.iconBox}>
            <div className={classes.iconWrapper}>
              <People className={classes.icon} fontSize="large" />
            </div>
            <div className={classes.contentWrapper}>
              <Typography component="div" variant="h6">
                <strong>
                  <FormattedMessage id="SelectedTimeSlotDetails.specialist" />
                </strong>
              </Typography>
              <Typography component="div" variant="h6">
                {selectedEmployee.firstName}
                {' '}
                {selectedEmployee.therapistName}
              </Typography>
              {(
                selectedEmployee.languages[intl.locale]
                || selectedEmployee.additionalInformation[intl.locale]
              ) && (
                <Box pt={1} />
              )}
              {selectedEmployee.languages[intl.locale] && (
                <Typography component="div">
                  <strong>
                    <FormattedMessage id="SelectedTimeSlotDetails.specialistLanguages" />
                  </strong>
                  {selectedEmployee.languages[intl.locale]}
                </Typography>
              )}
              {selectedEmployee.additionalInformation[intl.locale] && (
                <Typography component="div">
                  <strong>
                    <FormattedMessage id="SelectedTimeSlotDetails.specialistAdditionalInformation" />
                  </strong>
                  {selectedEmployee.additionalInformation[intl.locale]}
                </Typography>
              )}
            </div>
          </div>
        </Box>
      )}

      {selectedClinic && (
        <div className={classes.iconBox}>
          <div className={classes.iconWrapper}>
            <LocationOn className={classes.icon} fontSize="large" />
          </div>
          <div className={classes.contentWrapper}>
            <Box mb={2}>
              <Typography component="div" variant="h6">
                <strong>
                  {selectedClinic.nom}
                </strong>
              </Typography>
              <Typography component="div">
                {selectedClinic.adr_no}
                {' '}
                {selectedClinic.adr_rue}
              </Typography>
              <Typography component="div">{selectedClinic.adr_app}</Typography>
              <Typography component="div">
                {selectedClinic.adr_ville}
                ,
                {' '}
                {selectedClinic.adr_province}
                {' '}
                {selectedClinic.adr_cp}
              </Typography>
              <Typography component="div">
                <a href={`tel:+1${selectedClinic.tel}`} className={classes.phoneLink}>
                  {formatPhoneNumber(selectedClinic.tel)}
                </a>
              </Typography>
            </Box>

            <Box mb={2}>
              <Typography component="div">
                {(isLoadingPrice)
                  ? <CircularProgress size={20} />
                  : renderUiPrice()}
              </Typography>
            </Box>
          </div>
        </div>
      )}

      <Divider />

      <Box mt={3} mb={3}>
        <Typography component="div" variant="body2" className={classes.cancellationPolicyTitle}>
          <FormattedMessage id="SelectedTimeSlotDetails.cancellationPolicyTitle" />
        </Typography>
        <Typography component="div" className={classes.cancellationPolicyText}>
          <FormattedMessage id="SelectedTimeSlotDetails.cancellationPolicyText" />
        </Typography>
      </Box>
    </>
  );
};

SelectedTimeSlotDetails.propTypes = {};
export default SelectedTimeSlotDetails;
